/**
 * English translations for RoboGame.
 * Keys use flat dot-notation: 'section.subsection.key'
 * Interpolation: {{var}} placeholders replaced at runtime.
 */
export const en: Record<string, string> = {

  // ══════════════════════════════════════
  //  TITLE SCENE
  // ══════════════════════════════════════
  'title.campaign': 'CAMPAIGN',
  'title.stats': 'STATS',
  'title.challenge': 'CHALLENGE',
  'title.load': 'LOAD',
  'title.mapEditor': 'MAP EDITOR',
  'title.options': 'OPTIONS',
  'title.dev': 'DEV',
  'title.compGod': 'COMP GOD',
  'title.weapons': 'WEAPONS',
  'title.god': 'GOD',
  'title.robotLab': 'ROBOT LAB',
  'title.enemyScales': 'ENEMY SCALES',
  'title.balance': 'BALANCE',
  'title.exportConfig': 'EXPORT DEVCONFIG',
  'title.importConfig': 'IMPORT DEVCONFIG',
  'title.invalidConfig': 'Invalid devconfig file.',
  'title.reset': 'RESET',
  'title.resetTooltip': 'Clear all local data',
  'title.resetConfirm': 'Clear localStorage?',
  'title.resetWarning': 'All saves and settings will be lost.',
  'title.prototype': 'Early prototype — everything can change!',

  // ══════════════════════════════════════
  //  OPTIONS PANEL
  // ══════════════════════════════════════
  'options.title': 'OPTIONS',
  'options.audio': 'AUDIO',
  'options.display': 'DISPLAY',
  'options.sfxTest': 'SFX TEST',
  'options.music': 'MUSIC',
  'options.gameplay': 'GAME',
  'options.fxVolume': 'FX Volume',
  'options.musicVolume': 'Music Volume',
  'options.crt': 'CRT Effect',
  'options.scanlines': 'Scanlines',
  'options.warp': 'Warp',
  'options.vignette': 'Vignette',
  'options.difficulty': 'Difficulty',
  'options.mute': 'MUTE ALL',
  'options.unmute': 'UNMUTE',
  'options.fullscreen': 'FULLSCREEN',
  'options.exitFullscreen': 'EXIT FULLSCREEN',
  'options.autoScale': 'Game auto-scales to fit your window.',
  'options.crtOn': 'CRT EFFECT: ON',
  'options.crtOff': 'CRT EFFECT: OFF',
  'options.crtDesc': 'Scanlines, barrel distortion & vignette.',
  'options.resetToDefaults': 'RESET TO DEFAULTS',
  'options.resetToDefaultsNote': 'Restores all tuning parameters\nto the shipped defaults.',

  // ══════════════════════════════════════
  //  HUD (UIScene top bar)
  // ══════════════════════════════════════
  'hud.wave': 'Wave: {{n}}',
  'hud.waveOf': 'Wave: {{n}}/{{total}}',
  'hud.enemies': 'Enemies: {{n}}',
  'hud.base': 'Base:',
  'hud.startWave': 'Start Wave',
  'hud.pause': 'PAUSE',
  'hud.phase.workshop': 'WORKSHOP',
  'hud.phase.wave': 'WAVE {{n}}',
  'hud.phase.placing': 'PLACING...',
  'hud.phase.placingCustom': 'PLACING CUSTOM...',

  // ══════════════════════════════════════
  //  PAUSE MENU
  // ══════════════════════════════════════
  'pause.paused': 'PAUSED',
  'pause.menu': 'MENU',
  'pause.gameplay': 'GAMEPLAY',
  'pause.resume': 'RESUME',
  'pause.restart': 'RESTART',
  'pause.quitToTitle': 'QUIT',
  'pause.gameOptions': 'GAME OPTIONS',
  'pause.resetTutorial': 'RESET TUTORIAL',
  'pause.tutorialAlreadyActive': 'TUTORIAL ALREADY ACTIVE',
  'pause.tutorialReset': 'TUTORIAL RESET!',
  'pause.tutorialNote': 'The tutorial will start at the\nnext beginning of a game.',
  'pause.contextHelp': 'Context help',

  // ══════════════════════════════════════
  //  SOUND PANEL
  // ══════════════════════════════════════
  'sound.fx': 'FX',
  'sound.music': 'Music',
  'sound.mute': 'MUTE',
  'sound.unmute': 'UNMUTE',

  // ══════════════════════════════════════
  //  WAVE CLEAR SCREEN
  // ══════════════════════════════════════
  'waveClear.cleared': 'WAVE CLEARED!',
  'waveClear.tamed': 'WAVE TAMED!',
  'waveClear.waveCompleted': 'Wave {{n}}/{{total}} completed',
  'waveClear.workshop': 'WORKSHOP',
  'waveClear.deploy': 'DEPLOY',
  'waveClear.nextWave': 'NEXT WAVE',
  'waveClear.workshopDesc': 'Create / modify a robot design [Shift]',
  'waveClear.deployDesc': 'Place robots on the battlefield',
  'waveClear.nextWaveDesc': 'Start the next wave immediately',
  'waveClear.total': 'TOTAL',
  'waveClear.baseReward': 'Base Reward',
  'waveClear.resources': 'Resources',
  'waveClear.scoreTotal': 'SCORE TOTAL',
  'waveClear.stars': '{{n}} / 3 Stars',
  'waveClear.noStars': 'No Stars',
  'waveClear.rankBonus': '{{tier}} Bonus (1st time)',
  'waveClear.rerunNote': '(rerun #{{n}} — base reward reduced)',
  'waveClear.tierPlatinum': 'Platinum',
  'waveClear.tierGold': 'Gold',
  'waveClear.tierSilver': 'Silver',
  'waveClear.tierRank': 'Rank',

  // ══════════════════════════════════════
  //  GAME OVER
  // ══════════════════════════════════════
  'gameOver.victory': 'VICTORY!',
  'gameOver.defeat': 'DEFEAT',
  'gameOver.backToTitle': 'BACK TO TITLE',
  'gameOver.retry': 'RETRY',
  'gameOver.nextLevel': 'NEXT LEVEL',

  // ══════════════════════════════════════
  //  WORKSHOP PANEL (UIScene side panel)
  // ══════════════════════════════════════
  'workshop.units': 'UNITS',
  'workshop.myDesigns': 'MY DESIGNS',
  'workshop.sell': 'SELL',
  'workshop.cancel': 'CANCEL',
  'workshop.repair': 'REPAIR',
  'workshop.rebuild': 'REBUILD',
  'workshop.modify': 'MODIFY',
  'workshop.repaired': 'REPAIRED!',
  'workshop.notEnoughMoney': 'NOT ENOUGH $',

  // ══════════════════════════════════════
  //  ROSTER PANEL
  // ══════════════════════════════════════
  'roster.deployed': 'DEPLOYED ({{n}})',
  'roster.repairAll': 'REPAIR ALL -${{cost}}',
  'roster.scroll': '▼ scroll ({{n}})',

  // ══════════════════════════════════════
  //  BASE INSPECTOR
  // ══════════════════════════════════════
  'base.title': 'BASE',
  'base.hp': 'HP: {{current}} / {{max}}',
  'base.level': 'Level: {{n}}',
  'base.upgradePreview': '+{{hp}} HP  →  {{newMax}} max',
  'base.upgradeCost': 'Cost: ${{cost}}',
  'base.upgrade': 'UPGRADE',

  // ══════════════════════════════════════
  //  TILE REPAIR
  // ══════════════════════════════════════
  'tile.repairCost': 'REPAIR: ${{cost}}',
  'tile.unlockFirst': 'Unlock cheaper tiles first!',
  'tile.repair': 'REPAIR',
  'tile.repaired': 'REPAIRED',

  // ══════════════════════════════════════
  //  PLACEMENT ERRORS (other)
  // ══════════════════════════════════════
  'placement.spotTaken': 'SPOT TAKEN',
  'placement.noAnchor': 'TOO FAR FROM A FIXED UNIT!',
  'placement.waitIntermission': 'WAIT FOR INTERMISSION',
  'placement.noRobots': 'No robots deployed! Are you sure?',
  'placement.lastUnit': '⚠ LAST UNIT — $0 remaining after placement',
  'placement.budgetWarning': '⚠ BUDGET WARNING — not enough for another unit',

  // ══════════════════════════════════════
  //  INSUFFICIENT FUNDS — SASS TIERS
  // ══════════════════════════════════════
  // Tier 0 (polite)
  'funds.0.0': 'INSUFFICIENT FUNDS',
  'funds.0.1': 'NOT ENOUGH CASH',
  'funds.0.2': 'BUDGET EXCEEDED',
  'funds.0.3': 'NEED MORE $$$',
  // Tier 1 (sarcastic)
  'funds.1.0': 'STILL BROKE, HUH?',
  'funds.1.1': 'WALLET SAYS NO... AGAIN',
  'funds.1.2': 'MAYBE TRY EARNING FIRST?',
  'funds.1.3': 'CLICKING HARDER WON\'T HELP',
  'funds.1.4': 'MATH ISN\'T YOUR THING?',
  // Tier 2 (mocking)
  'funds.2.0': 'KEEP CLICKING, GENIUS',
  'funds.2.1': 'MAYBE SELL A KIDNEY?',
  'funds.2.2': 'YOUR BUDGET CALLED, IT\'S CRYING',
  'funds.2.3': 'ERROR 402: PAYMENT REQUIRED',
  'funds.2.4': 'HAVE YOU TRIED BEING RICH?',
  'funds.2.5': 'BROKE AND PERSISTENT... NICE',
  // Tier 3 (aggressive)
  'funds.3.0': 'YOU CALL THIS MANAGEMENT?!',
  'funds.3.1': 'YOUR ACCOUNTANT QUIT',
  'funds.3.2': 'THE BANK BLOCKED YOUR NUMBER',
  'funds.3.3': 'BANKRUPT SPEEDRUN ANY%',
  'funds.3.4': 'FINANCIAL DISASTER DETECTED',
  'funds.3.5': 'EVEN THE ENEMIES PITY YOU',
  'funds.3.6': 'YOUR ROBOTS WANT A NEW BOSS',
  // Tier 4 (existential)
  'funds.4.0': 'IS THIS SOME KIND OF PROTEST?',
  'funds.4.1': 'AT THIS POINT IT\'S PERFORMANCE ART',
  'funds.4.2': 'THE DEFINITION OF INSANITY',
  'funds.4.3': 'YOU KNOW THE $ SIGN RIGHT?',
  'funds.4.4': 'I ADMIRE YOUR STUBBORNNESS',
  'funds.4.5': 'ROBOT FOUNDRY BANKRUPTCY SIMULATOR',
  'funds.4.6': 'CEO OF WASTING CLICKS',
  'funds.4.7': 'THIS TILE FILED A RESTRAINING ORDER',

  // ══════════════════════════════════════
  //  MAP SELECT SCENE
  // ══════════════════════════════════════
  'mapSelect.title': 'MAP SELECTION',
  'mapSelect.official': 'OFFICIAL',
  'mapSelect.custom': 'CUSTOM',
  'mapSelect.back': '← BACK',
  'mapSelect.start': 'START',
  'mapSelect.challengeRules': 'CHALLENGE RULES',

  // Challenge intro dialog
  'challenge.introTitle': 'CHALLENGE MODE',
  'challenge.introLine1': '• All components unlocked in Campaign are available',
  'challenge.introLine2': '• No upgrades — all components stay at base level',
  'challenge.introLine3': '• No currency earned — play for the challenge!',
  'challenge.introLine4': '• Grid size matches your Campaign progress',
  'challenge.introLine5': '• Fixed starting budget per map',
  'challenge.introOk': 'GOT IT',
  'mapSelect.completeToReveal': 'COMPLETE TO REVEAL',
  'mapSelect.edit': '✎ EDIT',
  'mapSelect.delete': '✕ DELETE',
  'mapSelect.export': '↓ EXPORT',
  'mapSelect.noCustomMaps': 'No custom maps yet.\nUse the Map Editor to create one,\nor import a friend\'s map!',

  // ══════════════════════════════════════
  //  LEVEL TREE SCENE (Campaign)
  // ══════════════════════════════════════
  'levelTree.campaign': 'CAMPAIGN',
  'levelTree.back': '← BACK',
  'levelTree.deploy': 'DEPLOY',
  'levelTree.locked': 'LOCKED',
  'levelTree.editMap': '✎ EDIT MAP',

  // ══════════════════════════════════════
  //  WORKSHOP SCENE
  // ══════════════════════════════════════
  'workshopScene.title': 'WORKSHOP',
  'workshopScene.instructions': 'Drag components onto the grid · Right-click to remove',
  'workshopScene.stats': 'STATS',
  'workshopScene.cost': 'Cost',
  'workshopScene.mass': 'Mass',
  'workshopScene.hp': 'HP',
  'workshopScene.energy': 'Energy',
  'workshopScene.damage': 'Damage',
  'workshopScene.behavior': '⬆ BEHAVIOR',
  'workshopScene.behaviorHide': '▼ BEHAVIOR',
  'workshopScene.addRule': '[ + Add ]',
  'workshopScene.noRules': 'No rules.',
  'workshopScene.if': 'IF',
  'workshopScene.then': '→',
  'workshopScene.disconnected': 'Disconnected parts not allowed',

  // ══════════════════════════════════════
  //  GOD MODE SCENE
  // ══════════════════════════════════════
  'god.title': 'GOD MODE',
  'god.enemyScales': 'ENEMY SCALES',
  'god.resetAll': 'RESET ALL',
  'god.saveAll': 'SAVE ALL',
  'god.back': 'BACK',
  'god.resetConfirm': 'Reset all GOD params to defaults?',
  'god.saveConfirm': 'Save ALL tuning params?',
  // Category headers
  'god.cat.robot': 'ROBOT',
  'god.cat.economy': 'ECONOMY',
  'god.cat.workshop': 'WORKSHOP',
  'god.cat.wave': 'WAVE',
  'god.cat.scale': 'SCALE',
  'god.cat.bars': 'BARS',
  'god.cat.popup': 'POPUP',
  'god.cat.camera': 'CAMERA',
  'god.cat.cheat': 'CHEATS',

  // ══════════════════════════════════════
  //  GOD MODE IN-GAME PANEL (UIScene)
  // ══════════════════════════════════════
  'godPanel.title': '✦ GOD MODE ✦',
  'godPanel.enemyScaleEditor': 'ENEMY SCALE EDITOR',
  'godPanel.robotPreview': 'ROBOT PREVIEW',
  'godPanel.enemyPreview': 'ENEMY PREVIEW',
  'godPanel.damage': 'DAMAGE',
  'godPanel.repair': 'REPAIR',
  'godPanel.stun': 'STUN',
  'godPanel.attack': 'ATTACK',
  'godPanel.destroy': 'DESTROY',
  'godPanel.respawn': 'RESPAWN',
  'godPanel.on': 'ON',
  'godPanel.off': 'OFF',
  'godPanel.resetAll': 'RESET ALL',
  'godPanel.resetScales': 'RESET ALL SCALES',
  'godPanel.resetScalesConfirm': 'Reset all enemy scales?',
  'godPanel.playerProfile': '── PLAYER PROFILE ──',
  'godPanel.name': 'Name: {{name}}',
  'godPanel.age': 'Age: {{age}}',
  'godPanel.clickToSet': '(click to set)',
  'godPanel.exportProfile': 'EXPORT PROFILE',
  'godPanel.importProfile': 'IMPORT PROFILE',

  // ══════════════════════════════════════
  //  CONFIRM DIALOG DEFAULTS
  // ══════════════════════════════════════
  'dialog.confirm': 'Confirm',
  'dialog.cancel': 'Cancel',
  'dialog.ok': 'OK',

  // ══════════════════════════════════════
  //  ACHIEVEMENT TOAST
  // ══════════════════════════════════════
  'achievement.label': 'ACHIEVEMENT',

  // ══════════════════════════════════════
  //  ACHIEVEMENT PANEL
  // ══════════════════════════════════════
  'achievement.title': 'ACHIEVEMENTS',
  'achievement.completion': 'COMPLETION',
  'achievement.close': 'CLOSE',

  // Sarcastic rank titles (every 5% completion)
  'achievement.rank.0': 'Literally a Toaster',
  'achievement.rank.5': 'Found the ON Switch',
  'achievement.rank.10': 'Press More Buttons',
  'achievement.rank.15': 'Cute Attempt',
  'achievement.rank.20': 'Baby Steps',
  'achievement.rank.25': 'Still a Tourist',
  'achievement.rank.30': 'Mildly Interested',
  'achievement.rank.35': 'Casual+ Tier',
  'achievement.rank.40': 'Not Bad... I Guess',
  'achievement.rank.45': 'Above Average Andy',
  'achievement.rank.50': 'Half-Bot Half-Human',
  'achievement.rank.55': 'Getting Serious',
  'achievement.rank.60': 'OK Calm Down',
  'achievement.rank.65': 'Suspiciously Good',
  'achievement.rank.70': 'Dangerously Hooked',
  'achievement.rank.75': 'Sleep Is Optional',
  'achievement.rank.80': 'Addicted & Proud',
  'achievement.rank.85': 'Obsessed Engineer',
  'achievement.rank.90': 'No-Life Certified',
  'achievement.rank.95': 'Touch Grass Maybe?',
  'achievement.rank.100': 'Robot Overlord',

  // ══════════════════════════════════════
  //  ACHIEVEMENT CATEGORY LABELS
  // ══════════════════════════════════════
  'achievementCat.combat': 'COMBAT',
  'achievementCat.exploration': 'EXPLORATION',
  'achievementCat.economy': 'ECONOMY',
  'achievementCat.progression': 'PROGRESSION',
  'achievementCat.mastery': 'MASTERY',

  // ══════════════════════════════════════
  //  ACHIEVEMENT UI
  // ══════════════════════════════════════
  'achievement.unlocked': 'ACHIEVEMENT UNLOCKED',
  'achievement.claimHint': 'Open STATS to claim your reward!',

  // ══════════════════════════════════════
  //  ACHIEVEMENT NAMES & DESCRIPTIONS
  // ══════════════════════════════════════
  'achievement.total_kills.name': 'Pile \'Em Up!',
  'achievement.total_kills.desc': 'Destroy 500 enemies',
  'achievement.total_kills_g.name': 'Population Control',
  'achievement.total_kills_g.desc': 'Destroy 5,000 enemies',
  'achievement.grunt_kills.name': 'Show Some Gruntitude',
  'achievement.grunt_kills.desc': 'Destroy 200 Grunts',
  'achievement.tank_kills.name': 'No Tanks!',
  'achievement.tank_kills.desc': 'Destroy 100 Tanks',
  'achievement.flying_kills.name': 'Fly Swatter',
  'achievement.flying_kills.desc': 'Destroy 150 flying enemies',
  'achievement.elite_kills.name': 'Size Doesn\'t Matter',
  'achievement.elite_kills.desc': 'Destroy 50 elite or boss enemies',
  'achievement.best_streak.name': 'On a Roll!',
  'achievement.best_streak.desc': 'Get a 20-kill streak in one mission',
  'achievement.bases_destroyed.name': 'All Your Base Are Belong To Us',
  'achievement.bases_destroyed.desc': 'Destroy 50 enemy bases',
  'achievement.perfect_waves.name': 'Can\'t Touch This',
  'achievement.perfect_waves.desc': 'Complete 50 waves without base damage',
  'achievement.fog_explored.name': 'Where Am I?!',
  'achievement.fog_explored.desc': 'Uncover 5,000 fog tiles',
  'achievement.total_distance.name': 'Meep Meep!',
  'achievement.total_distance.desc': 'Robots travel 100,000 total distance',
  'achievement.robots_deployed.name': 'Bots Go Brrrr',
  'achievement.robots_deployed.desc': 'Deploy 100 robots into battle',
  'achievement.robots_lost.name': 'Oops... My Bad',
  'achievement.robots_lost.desc': 'Lose 50 robots in battle',
  'achievement.levels_completed.name': 'Mission Addicted',
  'achievement.levels_completed.desc': 'Complete 15 campaign missions',
  'achievement.total_stars.name': 'Twinkle Twinkle',
  'achievement.total_stars.desc': 'Earn 30 stars across all missions',
  'achievement.platinum_count.name': 'Shiny Hoarder',
  'achievement.platinum_count.desc': 'Earn 10 Platinum ratings',
  'achievement.components_unlock.name': 'Gotta Catch \'Em All',
  'achievement.components_unlock.desc': 'Unlock all components',
  'achievement.cartographer.name': 'Cartographer',
  'achievement.cartographer.desc': 'Unlock all 99 campaign maps',
  'achievement.evolutions.name': 'Not Even My Final Form',
  'achievement.evolutions.desc': 'Evolve 25 components',
  'achievement.overclocked.name': 'Overclocked',
  'achievement.overclocked.desc': 'Max-evolve 5 components',
  'achievement.hoarder.name': 'Hoarder',
  'achievement.hoarder.desc': 'Hold 500 Co + Electro at once',
  'achievement.speedrunner.name': 'Speedrunner',
  'achievement.speedrunner.desc': 'Complete 3 maps in under 2 minutes each (wave time only)',
  'achievement.minimalist.name': 'Minimalist',
  'achievement.minimalist.desc': 'Complete a map with only 1 robot',
  'achievement.reruns.name': 'Deja Vu... Again',
  'achievement.reruns.desc': 'Replay and complete 20 missions',
  'achievement.mission_accomplished.name': 'Mission Accomplished...?',
  'achievement.mission_accomplished.desc': 'Complete a map after all your robots are destroyed',
  'achievement.sweating_bullets.name': 'Sweating Bullets',
  'achievement.sweating_bullets.desc': 'Complete a map with base HP below 5%',
  'achievement.expendable.name': 'Expendable',
  'achievement.expendable.desc': 'Complete a map after losing 10+ robots',
  'achievement.untouchable.name': 'Untouchable',
  'achievement.untouchable.desc': 'Complete a map without base taking any damage',
  'achievement.immovable_object.name': 'Immovable Object',
  'achievement.immovable_object.desc': 'Complete a map using only fixed robots (stab legs)',
  'achievement.speedrun_fail.name': 'Speedrun (Gone Wrong)',
  'achievement.speedrun_fail.desc': 'Lose on the very first wave',
  'achievement.camper.name': 'Camper',
  'achievement.camper.desc': 'Destroy an enemy base with only fixed robots',
  'achievement.speedrun_wave1.name': 'SpeedRun (WRyyyyy)',
'achievement.speedrun_wave1.desc': 'Complete a map in under 30 seconds (wave time only)',
  'achievement.faster_than_light.name': 'Faster Than Light',
  'achievement.faster_than_light.desc': 'Complete a map in under 10 seconds',
  'achievement.theyre_just_numbers.name': 'They\'re Just Numbers',
  'achievement.theyre_just_numbers.desc': 'Lose 100 robots in a single mission',
  'achievement.afk.name': 'AFK',
  'achievement.afk.desc': 'Lose without deploying any robot',
  'achievement.im_helping.name': 'I\'m Helping!',
  'achievement.im_helping.desc': 'Complete a map with a support robot (no "real" weapons)',
  'achievement.grounded.name': 'Grounded',
  'achievement.grounded.desc': 'Complete a map without deploying any flying robot',
  'achievement.fly_me_to_the_moon.name': 'Fly Me to the Moon',
  'achievement.fly_me_to_the_moon.desc': 'Complete a map with only flying robots',
  'achievement.boom_surprise.name': 'Boom Surprise',
  'achievement.boom_surprise.desc': 'Kill 50 enemies with mines',
  'achievement.mine_kills_g.name': 'Minefield Maestro',
  'achievement.mine_kills_g.desc': 'Kill 200 enemies with mines',
  'achievement.cheater.name': 'Cheaters Never Prosper... Right?',
  'achievement.cheater.desc': 'Complete a map with a cheat code active',
  'achievement.damage_sponge.name': '\'Tis But a Scratch!',
  'achievement.damage_sponge.desc': 'Survive a single hit of 99+ damage',
  'achievement.one_punch.name': 'One Punch Bot',
  'achievement.one_punch.desc': 'Deal 99+ damage in a single hit',
  'achievement.combo_wave.name': 'Co-co-co-combo!',
  'achievement.combo_wave.desc': 'Get 5 combos (3+ streaks) in a single wave',
  'achievement.combo_master.name': 'Combo Meal',
  'achievement.combo_master.desc': 'Get 10 combos in a single wave',
  'achievement.ricochet_newbie.name': 'Rubber Duck',
  'achievement.ricochet_newbie.desc': 'Ricochet 50 enemy projectiles',
  'achievement.ricochet_master.name': 'No U',
  'achievement.ricochet_master.desc': 'Ricochet 500 enemy projectiles',
  'achievement.sharpshooter.name': 'Aimbot Accusation Incoming',
  'achievement.sharpshooter.desc': 'Finish a mission with 95%+ accuracy',
  'achievement.total_kills_s.name': 'Exterminator',
  'achievement.total_kills_s.desc': 'Destroy 1,000 enemies',
  'achievement.total_kills_p.name': 'Genocide Route',
  'achievement.total_kills_p.desc': 'Destroy 10,000 enemies',
  'achievement.best_streak_s.name': 'Killimanjaro',
  'achievement.best_streak_s.desc': 'Get a 10-kill streak in one mission',
  'achievement.all_achievements.name': 'Touch Grass? Never.',
  'achievement.all_achievements.desc': 'Unlock all other achievements',
  // Easter egg achievements
  'achievement.ee_pacifist.name': 'Pacifist Route',
  'achievement.ee_pacifist.desc': 'Violence was never the answer. But neither was this.',
  'achievement.ee_jenga.name': 'Jenga Champion',
  'achievement.ee_jenga.desc': 'Build a robot that defies physics (8+ tall column)',
  'achievement.ee_couch_potato.name': 'Couch Potato Pro',
  'achievement.ee_couch_potato.desc': 'Stay on the title screen for 5 minutes straight',
  'achievement.ee_discord.name': 'Persistence Is Key',
  'achievement.ee_discord.desc': 'Click the Discord button until it gives up',
  'achievement.hidden': '???',

  // ���═══════════════���══��═══════════���═════��
  //  COMPONENT NAMES
  // ══════════════════════════════════════
  'component.structure.name': 'Structure',
  'component.structure.desc': 'Basic chassis, adds HP. Light, ideal as foundation.',
  'component.armor.name': 'Armor',
  'component.armor.desc': 'Very tough but heavy — slows the robot. Blue shield absorbs damage first. Higher tiers ricochet projectiles.',
  'component.battery.name': 'Battery',
  'component.battery.desc': 'Essential: without energy, weapons and sensors won\'t work.',
  'component.wheel.name': 'Wheel',
  'component.wheel.desc': 'Adds mobility. More wheels = more speed, especially on heavy robots.',
  'component.thruster.name': 'Thruster',
  'component.thruster.desc': '2x faster than a wheel, but drains a lot of energy. Plan extra batteries.',
  'component.sensorShort.name': 'Radar',
  'component.sensorShort.desc': 'Short range (80px). Without a sensor, the robot can\'t see or shoot.',
  'component.sensorMedium.name': 'Sonar',
  'component.sensorMedium.desc': 'Medium range (160px). Lets you fire before the enemy gets too close.',
  'component.cannonLight.name': 'Cannon',
  'component.cannonLight.desc': 'Ground only. Good damage/cost ratio. Limited range. Low energy use.',
  'component.cannonHeavy.name': 'Howitzer',
  'component.cannonHeavy.desc': 'Ground only. Devastating but slow and heavy. Arc shells with AoE splash. Ideal on fixed turrets.',
  'component.explosiveCharge.name': 'Explosive',
  'component.explosiveCharge.desc': 'Destroys the robot! Maximize speed to charge into enemy groups.',
  'component.burstCannon.name': 'Burst',
  'component.burstCannon.desc': 'Ground+Air. 3-round burst then pause. Hits both. Good close-range DPS, overheats.',
  'component.repairModule.name': 'Repair',
  'component.repairModule.desc': 'Heals nearby allies & base. Range: 1 diag. Upgrades: +rate, +50% range, −energy.',
  'component.rechargeModule.name': 'Recharge',
  'component.rechargeModule.desc': 'Recharges ally energy. Range: 1 diag. Upgrades: +rate, +50% range, −energy.',
  'component.wing.name': 'Wings',
  'component.wing.desc': 'Free flight: ignores obstacles, moves diagonally. 2x faster than a wheel.',
  'component.missileSam.name': 'AA Missile',
  'component.missileSam.desc': 'Ground+Air. Arc-trajectory missile hitting both ground and air targets.',
  'component.missileSolSol.name': 'Missile',
  'component.missileSolSol.desc': 'Ground only. Long range. Can fire at enemies seen by allies. Arc trajectory.',
  'component.direction.name': 'Direction',
  'component.direction.desc': 'Focuses sensors into a cone: +50% range, +20% accuracy. Evolves to widen cone and boost further.',
  'component.structureTriangle.name': 'Bracket',
  'component.structureTriangle.desc': 'Right-angle bracket. Light, cheap. Only connects on 2 edges (the legs).',
  'component.flamethrower.name': 'Flames',
  'component.flamethrower.desc': 'Ground only. Very short range, continuous fire cone. Melts groups of enemies.',
  'component.laser.name': 'Laser',
  'component.laser.desc': 'Ground+Air. Instant beam, high damage. Very slow fire rate, heavy energy drain.',
  'component.stabLegs.name': 'Stabilizers',
  'component.stabLegs.desc': 'Stabilization legs. Fixed platform: 100% stability, +30% accuracy, 20% damage reduction. Incompatible with wheels/wings.',
  'component.turbine.name': 'Turbine',
  'component.turbine.desc': 'Helicopter flight: hover in place, ignore obstacles. Slower than wings but stable.',
  'component.mineLayer.name': 'Miner',
  'component.mineLayer.desc': 'Drops proximity mines. Low damage but stun + pushback. Chain reactions!',
  'component.rocketPod.name': 'Rockets',
  'component.rocketPod.desc': 'Ground+Air. Fires 5 homing missiles per salvo. Low precision, devastating vs groups.',
  'component.jumper.name': 'Jumper',
  'component.jumper.desc': 'Bottom: bouncing movement. Sides: push enemies/allies in jumper direction. Spring-loaded!',
  'component.excavator.name': 'Excavator',
  'component.excavator.desc': 'Harvests resources (Co, ◻) from nearby decorations. Passive, automatic.',
  'component.camouflage.name': 'Camouflage',
  'component.camouflage.desc': 'Makes the robot invisible to enemies. Drains energy continuously. Hologram effect.',

  // ══════════════════════════════════════
  //  BEHAVIOR LABELS
  // ══════════════════════════════════════
  // Conditions
  'behavior.condition.enemyDetected': 'Enemy detected',
  'behavior.condition.enemyClose': 'Enemy close',
  'behavior.condition.noEnemyDetected': 'No enemy',
  'behavior.condition.enemyInZone': 'Enemy in zone',
  'behavior.condition.lowEnergy': 'Low energy',
  'behavior.condition.allyLowEnergy': 'Ally low energy',
  'behavior.condition.allyNearbyDamaged': 'Ally damaged',
  'behavior.condition.baseDamaged': 'Base damaged',
  'behavior.condition.resourceDetected': 'Resource detected',
  'behavior.condition.always': 'Always',
  // Actions
  'behavior.action.shoot': 'Shoot',
  'behavior.action.advance': 'Advance',
  'behavior.action.retreat': 'Retreat',
  'behavior.action.holdPosition': 'Hold position',
  'behavior.action.patrol': 'Patrol',
  'behavior.action.protectAlly': 'Protect ally',
  'behavior.action.fallBack': 'Fall back',
  'behavior.action.explode': 'Explode',
  'behavior.action.repairAlly': 'Repair ally',
  'behavior.action.rechargeAlly': 'Recharge ally',
  'behavior.action.chase': 'Chase',
  'behavior.action.seekRecharger': 'Seek recharger',
  'behavior.action.repairBase': 'Repair base',
  'behavior.action.attackBase': 'Attack base',
  'behavior.action.pursue': 'Charge',
  'behavior.action.harvest': 'Harvest',
  // Target modifiers
  'behavior.target.closest': 'Closest',
  'behavior.target.mostAdvanced': 'Most advanced',

  // Popup titles
  'behavior.selectCondition': 'Select Condition',
  'behavior.selectAction': 'Select Action',
  'behavior.distance': 'Distance',

  // Condition descriptions
  'behavior.conditionDesc.enemyDetected': 'Any enemy within sensor range',
  'behavior.conditionDesc.enemyClose': 'Enemy within distance threshold',
  'behavior.conditionDesc.noEnemyDetected': 'No enemies visible',
  'behavior.conditionDesc.enemyInZone': 'Enemy entered sensor zone',
  'behavior.conditionDesc.lowEnergy': 'Robot energy below 20%',
  'behavior.conditionDesc.allyLowEnergy': 'Nearby ally energy below 30%',
  'behavior.conditionDesc.allyNearbyDamaged': 'Nearby ally HP not full',
  'behavior.conditionDesc.baseDamaged': 'Friendly base taking damage',
  'behavior.conditionDesc.resourceDetected': 'Harvestable resource within sensor range (needs Excavator)',
  'behavior.conditionDesc.always': 'Always triggers (fallback)',

  // Action descriptions
  'behavior.actionDesc.shoot': 'Fire weapons at target',
  'behavior.actionDesc.advance': 'Move toward enemy',
  'behavior.actionDesc.retreat': 'Move away from threats',
  'behavior.actionDesc.holdPosition': 'Stay in current position',
  'behavior.actionDesc.patrol': 'Patrol back and forth',
  'behavior.actionDesc.protectAlly': 'Move near and protect ally',
  'behavior.actionDesc.fallBack': 'Retreat to friendly base',
  'behavior.actionDesc.explode': 'Self-destruct (explosive charge)',
  'behavior.actionDesc.repairAlly': 'Repair nearby damaged ally',
  'behavior.actionDesc.rechargeAlly': 'Recharge nearby ally energy',
  'behavior.actionDesc.chase': 'Pursue and attack target',
  'behavior.actionDesc.seekRecharger': 'Move to recharge station',
  'behavior.actionDesc.repairBase': 'Repair friendly base',
  'behavior.actionDesc.attackBase': 'Attack enemy spawn base',
  'behavior.actionDesc.pursue': 'Full speed charge. Auto-detonates on contact if has explosive.',
  'behavior.actionDesc.harvest': 'Move toward nearest harvestable resource (needs Excavator)',

  // Validation warnings
  'behavior.warn.needsExcavator': '\u26A0 Requires Excavator',
  'behavior.warn.needsWeapon': '\u26A0 Requires weapon',
  'behavior.warn.needsExplosive': '\u26A0 Requires explosive',
  'behavior.warn.needsRepair': '\u26A0 Requires repair module',
  'behavior.warn.needsRecharge': '\u26A0 Requires recharge module',
  'behavior.warn.needsMovement': '\u26A0 Requires movement',
  'behavior.warn.needsEnergy': '\u26A0 No energy consumer',

  // ══════════════════════════════════════
  //  ROBOT PRESETS (Templates)
  // ══════════════════════════════════════
  'preset.sentry': 'Sentry',
  'preset.turret': 'Turret',
  'preset.tank': 'Tank',
  'preset.hunter': 'Hunter',
  'preset.bomber': 'Bomber',
  'preset.scout': 'Scout',
  'preset.charger': 'Charger',
  'preset.medic': 'Medic',
  'preset.scorcher': 'Scorcher',
  'preset.sniper': 'Sniper',
  'preset.copter': 'Copter',
  'preset.mine': 'Mine',
  'preset.missile': 'Missile',
  'preset.fortress': 'Fortress',
  'preset.blocker': 'Blocker',
  'preset.raider': 'Raider',
  'preset.launcher': 'Launcher',
  'preset.juggernaut': 'Juggernaut',
  'preset.skyguard': 'Skyguard',
  'preset.ambulance': 'Ambulance',
  'preset.titan': 'Titan',
  'preset.citadel': 'Citadel',
  'preset.carrier': 'Carrier',

  // ══════════════════════════════════════
  //  EXPERIENCE LEVELS
  // ══════════════════════════════════════
  'exp.beginner': 'beginner',
  'exp.casual': 'casual',
  'exp.regular': 'regular',
  'exp.experienced': 'experienced',
  'exp.hardcore': 'hardcore',

  // ══════════════════════════════════════
  //  STATS PANEL (TitleScene)
  // ══════════════════════════════════════
  'stats.title': 'STATISTICS',
  'stats.totalKills': 'Total Kills',
  'stats.totalDeaths': 'Robots Lost',
  'stats.missionsCompleted': 'Missions Completed',
  'stats.totalStars': 'Total Stars',
  'stats.bestStreak': 'Best Streak',

  // ══════════════════════════════════════
  //  SLOTS PANEL
  // ══════════════════════════════════════
  'slots.title': 'SAVE SLOTS',
  'slots.empty': 'Empty Slot',
  'slots.new': 'NEW GAME',
  'slots.continue': 'CONTINUE',
  'slots.delete': 'DELETE',
  'slots.deleteConfirm': 'Delete this save slot?',

  // ══════════════════════════════════════
  //  ENEMY SCALE SCENE
  // ══════════════════════════════════════
  'enemyScale.title': 'ENEMY SCALE EDITOR',
  'enemyScale.resetAll': 'RESET ALL',
  'enemyScale.resetConfirm': 'Reset all enemy scales & anchors?',
  'enemyScale.back': 'BACK',

  // ══════════════════════════════════════
  //  MISC
  // ══════════════════════════════════════
  'misc.back': 'BACK',
  'misc.close': '✕',
  'misc.on': 'ON',
  'misc.off': 'OFF',

  // ══════════════════════════════════════
  //  TUTORIAL STEPS
  // ══════════════════════════════════════
  'tutorial.base': 'This is your base!\nProtect it at all costs.',
  'tutorial.enemyBase': 'This is the enemy base.\nWaves of enemies come from here.',
  'tutorial.path': 'Enemies follow this path\nto reach your base.',
  'tutorial.placements': 'Fixed robots are placed on\nthese metal platforms.',
  'tutorial.mobileInfo': 'Mobile robots can be deployed\nelsewhere on the battlefield.',
  'tutorial.brokenPlacements': 'These platforms are damaged.\nRepair them to place robots.',
  'tutorial.special': 'Watch out for special tiles:\nmud (slows), water (blocks),\nmagma (deals damage).',
  'tutorial.walls': 'Walls block movement.',
  'tutorial.selectPreset': 'Choose a robot to deploy!',
  'tutorial.placeRobot': 'Click on a platform\nto place your robot.',
  'tutorial.startWave': 'Launch the first wave!',
  'tutorial.welcome': 'Welcome, Commander!\nLet me brief you on the battlefield\nbefore your first deployment.',
  'tutorial.skip': 'SKIP',
  'tutorial.clickContinue': '[ click to continue ]',

  // ══════════════════════════════════════
  //  WORKSHOP TUTORIAL
  // ══════════════════════════════════════
  'tutorialWs.welcome': 'Welcome to the Workshop!\nDesign custom robots from scratch.',
  'tutorialWs.grid': 'This is the assembly grid.\nDrag components here to build\nyour robot\'s body.',
  'tutorialWs.palette': 'Components are listed here.\nDrag them onto the grid\nto assemble your robot.',
  'tutorialWs.stats': 'Your robot\'s stats update\nin real-time as you add components.',
  'tutorialWs.behavior': 'Configure AI behavior rules.\nYour robot will follow these\nin combat.',
  'tutorialWs.save': 'When your design is ready,\nsave it to deploy on the battlefield.',

  // ── Map Editor: campaign overrides ──
  'editor.saveToCampaign': 'Save to Campaign',
  'editor.resetToFactory': 'Reset to Factory',
  'editor.campaignSaved': 'Campaign override saved for "{{name}}".',

  // ── Cursor hints (context-sensitive guide) ──
  // Phase guidance
  'cursor.workshopChoose': 'WORKSHOP — Pick a robot or create a design',
  'cursor.workshopPlace': 'Click to deploy here',
  'cursor.workshopCantPlace': 'Cannot deploy here',
  'cursor.workshopStartWave': 'Ready? Press SPACE to launch the wave',
  'cursor.waveInProgress': 'COMBAT — Wave in progress',
  'cursor.intermissionDeploy': 'Deploy reinforcements before next wave',
  // Placement
  'cursor.placingValid': 'LMB Deploy · RMB Cancel',
  'cursor.placingInvalid': 'Invalid terrain · RMB Cancel',
  // Entities
  'cursor.robotInfo': '{{name}} — HP {{hp}}/{{max}}',
  'cursor.robotSelect': 'LMB Select · Sell / Repair / Modify',
  'cursor.enemyInfo': '{{type}} — HP {{hp}}/{{max}}',
  'cursor.enemySpawn': 'ENEMY BASE — Spawn point',
  'cursor.baseInfo': 'BASE — HP {{hp}}/{{max}}',
  'cursor.baseUpgrade': 'LMB Inspect · Upgrade available',
  // Resources & decorations
  'cursor.resourceInfo': '{{type}} — Harvest: {{yield}}',
  'cursor.decorObstacle': '{{type}} — Obstacle',
  'cursor.decorHazard': '{{type}} — Hazard zone',
  // Terrain
  'cursor.crackedTile': 'Damaged tile — LMB Repair',
  'cursor.fogHidden': 'Unexplored',
  // Dev
  'cursor.devNuke': 'DEV: LMB Nuke',
  'cursor.devDestroy': 'DEV: LMB Destroy',
  'cursor.devMove': 'DEV: Drag to move',

  // ── Decoration names ──
  'decor.tree': 'Tree',
  'decor.ruins': 'Ruins',
  'decor.column': 'Column',
  'decor.chasm': 'Chasm',
  'decor.conduit': 'Conduit',
  'decor.barrel': 'Barrel',
  'decor.broken_wall': 'Broken Wall',
  'decor.caisse': 'Crate',
  'decor.dead_tree': 'Dead Tree',
  'decor.flaque': 'Puddle',
  'decor.rocher': 'Rock',
  'decor.souche': 'Stump',
  'decor.connardium': 'Connardium',
  'decor.garbage': 'Garbage',
  'decor.barriere': 'Fence',
  'decor.broken_statue': 'Broken Statue',

  // ── Enemy names ──
  'enemy.grunt': 'Grunt',
  'enemy.runner': 'Runner',
  'enemy.tank': 'Tank',
  'enemy.swarm': 'Swarm',
  'enemy.brute': 'Brute',
  'enemy.phantom': 'Phantom',
  'enemy.drone': 'Drone',
  'enemy.bomber': 'Bomber',
  'enemy.wasp': 'Wasp',
  'enemy.specter': 'Specter',

  // ══════════════════════════════════════
  //  CINEMATIC TUTORIAL
  // ══════════════════════════════════════
  'cinematic.skip': 'SKIP',
  'cinematic.speaker.operator': 'OPERATOR',
  'cinematic.speaker.commander': 'CDT. MUSTANG',
  'cinematic.speaker.narrator': 'NARRATOR',

  // Demo Wave 1
  'cinematic.dw1.alert': 'COMMANDER, WE ARE UNDER ATTACK!!!',
  'cinematic.dw1.enemyIncoming': 'The enemy is charging toward the base!!!',
  'cinematic.dw1.deployTurret': 'Deploying a turret pushes them back!!!',
  'cinematic.dw1.recapTitle': 'You just saw:',
  'cinematic.dw1.recapEnemies': 'Enemies spawn from red bases and follow paths',
  'cinematic.dw1.recapPath': 'They march toward your base to destroy it',
  'cinematic.dw1.recapTurret': 'Turrets placed on platforms fire automatically',
  'cinematic.dw1.recapConfirm': 'Got it! Place one yourself?',

  // ══════════════════════════════════════
  //  PROTOTYPE STAMP (Title Screen)
  // ══════════════════════════════════════
  'stamp.prototype': 'PROTOTYPE',
  'stamp.wip': 'WORK IN PROGRESS',
  'stamp.comeback': 'IF SOMETHING BAD HAPPENS ➜ COME BACK LATER',

  // ══════════════════════════════════════
  //  DISCORD EASTER EGG
  // ══════════════════════════════════════
  'title.discord': 'JOIN THE DISCORD',
  'title.discord.1': "JUST KIDDING I DON'T HAVE DISCORD",
  'title.discord.2': 'STOP CLICKING. THERE IS NO DISCORD.',
  'title.discord.3': 'OK FINE, YOU WIN. HERE IS YOUR DISCORD:',
  'title.discord.4': '...JUST KIDDING AGAIN. LOL.',
  'title.discord.5': "YOU'RE STILL HERE? GO PLAY THE GAME!",

  // ══════════════════════════════════════
  //  EMAIL SUBSCRIBE
  // ══════════════════════════════════════
  'subscribe.placeholder': 'your@email.com',
  'subscribe.button': 'SUBSCRIBE',
  'subscribe.label': 'Subscribe to stay updated!',
  'subscribe.gdpr': 'I agree to receive emails and accept the privacy policy.',
  'subscribe.gdprTitle': 'PRIVACY POLICY',
  'subscribe.gdprText': 'We collect your email address solely to send you game updates and news about TetsuRobot. Your email is stored securely on Cloudflare and will never be shared with third parties. You can unsubscribe at any time by contacting us. By checking this box, you consent to the processing of your personal data in accordance with the EU General Data Protection Regulation (GDPR).',
  'subscribe.gdprClose': 'CLOSE',
  'subscribe.success': 'SUBSCRIBED!',
  'subscribe.alreadySubscribed': 'ALREADY SUBSCRIBED',
  'subscribe.error': 'ERROR — TRY AGAIN',
  'subscribe.gdprRequired': 'PLEASE ACCEPT THE PRIVACY POLICY',

  // ══════════════════════════════════════
  //  TITLE DEV BUTTONS
  // ══════════════════════════════════════
  'title.animations': 'Animations',
  'title.changelog': 'CHANGELOG',
  'title.changelog.close': 'CLOSE',

  // ══════════════════════════════════════
  //  STATS PANEL TABS
  // ══════════════════════════════════════
  'stats.achievements': 'ACHIEVEMENTS',
  'stats.collection': 'COLLECTION',

  // ══════════════════════════════════════
  //  SFX TEST CATEGORIES
  // ══════════════════════════════════════
  'sfx.weapons': 'WEAPONS',
  'sfx.impacts': 'IMPACTS',
  'sfx.enemies': 'ENEMIES',
  'sfx.enemyFire': 'ENEMY FIRE',
  'sfx.robots': 'ROBOTS',
  'sfx.engineLoops': 'ENGINE LOOPS',
  'sfx.gameFlow': 'GAME FLOW',
  'sfx.rewards': 'REWARDS',
  'sfx.ui': 'UI',

  // ══════════════════════════════════════
  //  HUD SPEED
  // ══════════════════════════════════════
  'hud.speed': '>> x{{n}}',

  // ══════════════════════════════════════
  //  UI SCENE DEV
  // ══════════════════════════════════════
  'ui.cinematic': 'CINEMATIC',
  'pause.resetCinematics': 'Reset Cinematics',
  'pause.cinematicsReset': 'Cinematics reset!',

  // ══════════════════════════════════════
  //  MAP SELECT SCENE
  // ══════════════════════════════════════
  'mapSelect.createMap': '+ CREATE MAP',
  'mapSelect.importMap': '↑ IMPORT MAP FILE',
};
